/**
 * "We can't email you right now" notice.
 *
 * Mounted at the App level and renders nothing unless the server says the signed-in user's
 * address is currently undeliverable — the same shape as ImpersonationBanner and UpdateBanner.
 *
 * Why it exists: when Apple private relay started rejecting our mail as an unauthorized sender,
 * users with a "Hide My Email" alias simply stopped receiving billing notices and nothing
 * anywhere told them. Silence is the worst possible failure mode for a billing warning.
 *
 * Two very different messages hide behind one notice, and getting them the wrong way round is
 * the mistake worth avoiding:
 *
 *   - Our configuration is broken → the user's address is FINE. Say so, do not offer to change
 *     it, and reassure them that push and in-app notifications still work. Dismissible, because
 *     there is nothing they can do.
 *   - Their address is bouncing → they can fix it, so link them to Settings, and do not let it
 *     be dismissed away.
 *
 * Positioned `fixed`, not in the normal flow: AppLayout locks html/body/#root to the viewport
 * height with `overflow: hidden` (body.app-layout in index.css), so anything in the flow above
 * <Routes> is simply clipped and never seen. Anchored above the 64px BottomNavigation and its
 * safe-area padding, and left-aligned to stay clear of UpdateBanner and the toast stack, which
 * both live in the bottom-right.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailWarning, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEmailDeliverability } from '@/api/extensions/useEmailDeliverability';

export function EmailUndeliverableBanner() {
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const { data } = useEmailDeliverability(isAuthenticated);

  if (!isAuthenticated || !data || data.deliverable) return null;

  const ourProblem = data.causedByOurConfiguration;

  // Only the "nothing you can do" variant is dismissible. An address the user could fix should
  // keep asking until they fix it.
  if (ourProblem && dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-4 right-4 z-50 max-w-md sm:right-auto"
      style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <div
        className={
          ourProblem
            ? 'flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 shadow-lg'
            : 'flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 shadow-lg'
        }
      >
        <MailWarning className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <span>{data.message}</span>
          {!ourProblem && (
            <>
              {' '}
              <Link to="/settings" className="font-semibold underline underline-offset-2">
                Update your email
              </Link>
            </>
          )}
        </div>

        {ourProblem && (
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss email delivery notice"
            className="shrink-0 rounded p-0.5 text-blue-700 transition-colors hover:bg-blue-100"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

export default EmailUndeliverableBanner;
